"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { ResponsiveContainer, Sankey, Tooltip } from "recharts";
import React from "react";
import { cx } from "@/lib/tailwind-utils";
import { CollapsibleHeight } from "./collapsible-height";

// Palette used for Sankey nodes — recharts Sankey doesn't use Tailwind classes
const SANKEY_COLORS: string[] = ["#3b82f6",
// blue
"#10b981",
// emerald
"#8b5cf6",
// violet
"#f59e0b",
// amber
"#f43f5e",
// rose
"#06b6d4",
// cyan
"#f97316",
// orange
"#6366f1",
// indigo
"#14b8a6",
// teal
"#ec4899" // pink
];
const MAX_LABEL_CHARS = 24;
function truncate(s: string): string {
  return s.length > MAX_LABEL_CHARS ? s.slice(0, MAX_LABEL_CHARS - 1) + "…" : s;
}
interface SankeyNode {
  name: string;
}
interface SankeyLink {
  source: number;
  target: number;
  value: number;
}
export interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}
interface SankeyNodeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
  payload?: any;
  containerWidth?: number;
}

/** Custom node renderer — labels left of source nodes, right of target nodes */
const SankeyNodeRenderer = t0 => {
  const $ = _c(19);
  const {
    x: t1,
    y: t2,
    width: t3,
    height: t4,
    index: t5,
    payload,
    containerWidth: t6
  } = t0;
  const x = t1 === undefined ? 0 : t1;
  const y = t2 === undefined ? 0 : t2;
  const width = t3 === undefined ? 10 : t3;
  const height = t4 === undefined ? 0 : t4;
  const index = t5 === undefined ? 0 : t5;
  const containerWidth = t6 === undefined ? 800 : t6;
  const color = SANKEY_COLORS[index % SANKEY_COLORS.length];
  const t7 = payload?.name ?? "";
  let t8;
  if ($[0] !== t7) {
    t8 = truncate(t7);
    $[0] = t7;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const name = t8;
  const isRightSide = x + width > containerWidth / 2;
  const fontSize = height < 10 ? 9 : 11;
  const t9 = Math.max(height, 1);
  let t10;
  if ($[2] !== color || $[3] !== t9 || $[4] !== width || $[5] !== x || $[6] !== y) {
    t10 = <rect x={x} y={y} width={width} height={t9} fill={color} fillOpacity={0.85} rx={2} />;
    $[2] = color;
    $[3] = t9;
    $[4] = width;
    $[5] = x;
    $[6] = y;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== fontSize || $[9] !== height || $[10] !== isRightSide || $[11] !== name || $[12] !== width || $[13] !== x || $[14] !== y) {
    t11 = height >= 4 && <text x={isRightSide ? x + width + 5 : x - 5} y={y + height / 2} fill="currentColor" fontSize={fontSize} dominantBaseline="middle" textAnchor={isRightSide ? "start" : "end"} className="fill-foreground-secondary">{name}</text>;
    $[8] = fontSize;
    $[9] = height;
    $[10] = isRightSide;
    $[11] = name;
    $[12] = width;
    $[13] = x;
    $[14] = y;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <g>{t10}{t11}</g>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
};
interface SankeyLinkProps {
  sourceX?: number;
  sourceY?: number;
  sourceControlX?: number;
  targetX?: number;
  targetY?: number;
  targetControlX?: number;
  linkWidth?: number;
  payload?: any;
}

/** Custom link renderer — colors each flow with its source node's color */
const SankeyLinkRenderer = t0 => {
  const $ = _c(3);
  const {
    sourceX: t1,
    sourceY: t2,
    sourceControlX: t3,
    targetX: t4,
    targetY: t5,
    targetControlX: t6,
    linkWidth: t7,
    payload
  } = t0;
  const sourceX = t1 === undefined ? 0 : t1;
  const sourceY = t2 === undefined ? 0 : t2;
  const sourceControlX = t3 === undefined ? 0 : t3;
  const targetX = t4 === undefined ? 0 : t4;
  const targetY = t5 === undefined ? 0 : t5;
  const targetControlX = t6 === undefined ? 0 : t6;
  const linkWidth = t7 === undefined ? 0 : t7;
  const sourceIndex = typeof payload?.source === "number" ? payload.source : payload?.source?.index ?? 0;
  const color = SANKEY_COLORS[sourceIndex % SANKEY_COLORS.length];
  const half = linkWidth / 2;
  const t8 = `
        M${sourceX},${sourceY - half}
        C${sourceControlX},${sourceY - half} ${targetControlX},${targetY - half} ${targetX},${targetY - half}
        L${targetX},${targetY + half}
        C${targetControlX},${targetY + half} ${sourceControlX},${sourceY + half} ${sourceX},${sourceY + half}
        Z
      `;
  let t9;
  if ($[0] !== color || $[1] !== t8) {
    t9 = <path d={t8} fill={color} fillOpacity={0.35} strokeWidth={0} />;
    $[0] = color;
    $[1] = t8;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  return t9;
};
interface SankeyTooltipProps {
  active?: boolean;
  payload?: any[];
  valueFormatter?: (v: number) => string;
}
const SankeyTooltip = ({
  active,
  payload,
  valueFormatter = v => v.toLocaleString()
}: SankeyTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  const isLink = item.source !== undefined;
  return <div className="rounded-md border border-border bg-background/90 px-3 py-2 text-body shadow-md backdrop-blur-2xl">
      {isLink ? <p className="text-foreground">
          <span className="font-medium">
            <span>{item.source?.name ?? item.source}</span>
            <span> →</span> <span>{item.target?.name ?? item.target}</span>
          </span>
          <span className="ml-2 tabular-nums text-foreground-secondary">
            {valueFormatter(item.value)}
          </span>
        </p> : <p className="font-medium text-foreground">
          {item.payload?.name ?? item.name}
        </p>}
    </div>;
};
export interface SankeyChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: SankeyData;
  valueFormatter?: (value: number) => string;
  nodePadding?: number;
  nodeWidth?: number;
}
const SankeyChart = React.forwardRef<HTMLDivElement, SankeyChartProps>((t0, forwardedRef) => {
  const $ = _c(23);
  let className;
  let data;
  let nodePadding;
  let other;
  let t1;
  let valueFormatter;
  if ($[0] !== t0) {
    ({
      data,
      valueFormatter,
      nodePadding,
      nodeWidth: t1,
      className,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = data;
    $[3] = nodePadding;
    $[4] = other;
    $[5] = t1;
    $[6] = valueFormatter;
  } else {
    className = $[1];
    data = $[2];
    nodePadding = $[3];
    other = $[4];
    t1 = $[5];
    valueFormatter = $[6];
  }
  const nodeWidth = t1 === undefined ? 12 : t1;
  const nodeCount = data.nodes.length;
  const effectivePadding = nodePadding ?? Math.max(4, Math.min(12, Math.floor(400 / nodeCount)));
  const chartHeight = Math.max(320, nodeCount * (effectivePadding + 14));
  let t2;
  if ($[7] !== className) {
    t2 = cx("w-full", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = `${nodeCount} nodes`;
  let t4;
  if ($[9] !== data || $[10] !== effectivePadding || $[11] !== nodeWidth || $[12] !== valueFormatter) {
    t4 = activeHeight => <div style={{
      height: activeHeight
    }} className="w-full overflow-hidden [&_svg]:overflow-hidden"><ResponsiveContainer width="100%" height="100%"><Sankey data={data} nodePadding={effectivePadding} nodeWidth={nodeWidth} link={_temp} node={_temp2} margin={{
          left: 160,
          right: 160,
          top: 8,
          bottom: 8
        }}><Tooltip content={t5 => {
            const {
              active,
              payload
            } = t5;
            return <SankeyTooltip active={active} payload={payload ? [...payload] : []} valueFormatter={valueFormatter} />;
          }} /></Sankey></ResponsiveContainer></div>;
    $[9] = data;
    $[10] = effectivePadding;
    $[11] = nodeWidth;
    $[12] = valueFormatter;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== chartHeight || $[15] !== t3 || $[16] !== t4) {
    t5 = <CollapsibleHeight height={chartHeight} expandLabel={t3}>{t4}</CollapsibleHeight>;
    $[14] = chartHeight;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== forwardedRef || $[19] !== other || $[20] !== t2 || $[21] !== t5) {
    t6 = <div ref={forwardedRef} className={t2} tremor-id="tremor-raw" {...other}>{t5}</div>;
    $[18] = forwardedRef;
    $[19] = other;
    $[20] = t2;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
});
SankeyChart.displayName = "SankeyChart";
export { SankeyChart };
function _temp(props) {
  return <SankeyLinkRenderer {...props} />;
}
function _temp2(props_0) {
  return <SankeyNodeRenderer {...props_0} containerWidth={props_0.containerWidth ?? 800} />;
}